/**
 * @file services/summaryDefinitions.ts
 * @description Service class for Summary Definition CRUD operations
 */

import { SUMMARY_DEFINITIONS } from 'common/endpoints';
import {
  SpreadsheetDefinition,
  SummaryDefinition,
  SummaryDefinitionListResponse,
  SummaryVisibility,
} from 'common/types/summaryDefinitions';
import BaseServiceRetryWrapper from './baseRetry';

export interface CreateSummaryDefinitionPayload {
  name: string;
  spreadsheet_definition: SpreadsheetDefinition;
  visibility?: SummaryVisibility;
  cloned_from?: number | null;
}

export interface UpdateSummaryDefinitionPayload {
  name?: string;
  spreadsheet_definition?: SpreadsheetDefinition;
  visibility?: SummaryVisibility;
}

export interface UpdateDefinitionOrderPayload {
  order: number[];
}

const EMPTY_SPREADSHEET_DEFINITION: SpreadsheetDefinition = {
  columns: {},
  sortKeys: [],
  groupBy: 'company',
  filters: [],
  filterExclusions: [],
};

const normalizeDefinition = (
  definition?: SpreadsheetDefinition | null
): SpreadsheetDefinition => ({
  ...EMPTY_SPREADSHEET_DEFINITION,
  ...(definition ?? {}),
  columns: definition?.columns ?? {},
  sortKeys: definition?.sortKeys ?? [],
  filters: definition?.filters ?? [],
  filterExclusions: definition?.filterExclusions ?? [],
  groupBy: definition?.groupBy ?? 'company',
});

class SummaryDefinitionsService extends BaseServiceRetryWrapper {
  // ============================================
  // Global Summary Definitions (Admin only)
  // ============================================

  /**
   * Get all global summary definitions
   */
  getGlobalDefinitions = (): Promise<SummaryDefinition[]> =>
    this.get(SUMMARY_DEFINITIONS.globalDefinitions) as Promise<SummaryDefinition[]>;

  /**
   * Get a global summary definition by ID
   */
  getGlobalDefinitionById = (id: number): Promise<SummaryDefinition> =>
    this.get(SUMMARY_DEFINITIONS.globalDefinitionById(id)) as Promise<SummaryDefinition>;

  /**
   * Create a new global summary definition (staff only)
   */
  createGlobalDefinition = (data: CreateSummaryDefinitionPayload): Promise<SummaryDefinition> =>
    this.post(SUMMARY_DEFINITIONS.globalDefinitions, data) as Promise<SummaryDefinition>;

  /**
   * Update a global summary definition (staff only)
   */
  updateGlobalDefinition = (id: number, data: UpdateSummaryDefinitionPayload): Promise<SummaryDefinition> =>
    this.put(SUMMARY_DEFINITIONS.globalDefinitionById(id), data) as Promise<SummaryDefinition>;

  /**
   * Delete a global summary definition (staff only)
   */
  deleteGlobalDefinition = (id: number): Promise<void> =>
    this.delete(SUMMARY_DEFINITIONS.globalDefinitionById(id)) as Promise<void>;

  // ============================================
  // Firm Summary Definitions
  // ============================================

  /**
   * Get all firm summary definitions (including global defaults)
   * Returns: { definitions, global_definitions, order }
   */
  getFirmDefinitions = (firmId: number): Promise<SummaryDefinitionListResponse> =>
    this.get(SUMMARY_DEFINITIONS.firmDefinitions(firmId)) as Promise<SummaryDefinitionListResponse>;

  /**
   * Get a specific firm summary definition by ID
   */
  getFirmDefinitionById = (firmId: number, definitionId: number): Promise<SummaryDefinition> =>
    this.get(SUMMARY_DEFINITIONS.firmDefinitionById(firmId, definitionId)) as Promise<SummaryDefinition>;

  /**
   * Create a new firm summary definition
   */
  createFirmDefinition = (firmId: number, data: CreateSummaryDefinitionPayload): Promise<SummaryDefinition> =>
    this.post(SUMMARY_DEFINITIONS.firmDefinitions(firmId), data) as Promise<SummaryDefinition>;

  /**
   * Update a firm summary definition
   */
  updateFirmDefinition = (
    firmId: number,
    definitionId: number,
    data: UpdateSummaryDefinitionPayload
  ): Promise<SummaryDefinition> =>
    this.patch(SUMMARY_DEFINITIONS.firmDefinitionById(firmId, definitionId), data) as Promise<SummaryDefinition>;

  /**
   * Delete a firm summary definition
   */
  deleteFirmDefinition = (firmId: number, definitionId: number): Promise<void> =>
    this.delete(SUMMARY_DEFINITIONS.firmDefinitionById(firmId, definitionId)) as Promise<void>;

  /**
   * Update the ordering of firm summary definition tabs
   */
  updateFirmDefinitionsOrder = (firmId: number, data: UpdateDefinitionOrderPayload): Promise<{ order: number[] }> =>
    this.put(SUMMARY_DEFINITIONS.firmDefinitionsOrder(firmId), data) as Promise<{ order: number[] }>;

  // ============================================
  // Fund Summary Definitions
  // ============================================

  /**
   * Get all fund summary definitions (including global defaults)
   * Returns: { definitions, global_definitions, order }
   */
  getFundDefinitions = (fundId: number): Promise<SummaryDefinitionListResponse> =>
    this.get(SUMMARY_DEFINITIONS.fundDefinitions(fundId)) as Promise<SummaryDefinitionListResponse>;

  /**
   * Get a specific fund summary definition by ID
   */
  getFundDefinitionById = (fundId: number, definitionId: number): Promise<SummaryDefinition> =>
    this.get(SUMMARY_DEFINITIONS.fundDefinitionById(fundId, definitionId)) as Promise<SummaryDefinition>;

  /**
   * Create a new fund summary definition
   */
  createFundDefinition = (fundId: number, data: CreateSummaryDefinitionPayload): Promise<SummaryDefinition> =>
    this.post(SUMMARY_DEFINITIONS.fundDefinitions(fundId), data) as Promise<SummaryDefinition>;

  /**
   * Update a fund summary definition
   */
  updateFundDefinition = (
    fundId: number,
    definitionId: number,
    data: UpdateSummaryDefinitionPayload
  ): Promise<SummaryDefinition> =>
    this.patch(SUMMARY_DEFINITIONS.fundDefinitionById(fundId, definitionId), data) as Promise<SummaryDefinition>;

  /**
   * Delete a fund summary definition
   */
  deleteFundDefinition = (fundId: number, definitionId: number): Promise<void> =>
    this.delete(SUMMARY_DEFINITIONS.fundDefinitionById(fundId, definitionId)) as Promise<void>;

  /**
   * Update the ordering of fund summary definition tabs
   */
  updateFundDefinitionsOrder = (fundId: number, data: UpdateDefinitionOrderPayload): Promise<{ order: number[] }> =>
    this.put(SUMMARY_DEFINITIONS.fundDefinitionsOrder(fundId), data) as Promise<{ order: number[] }>;

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Clone a summary definition (creates a copy with new name)
   * Works for both firm and fund definitions
   */
  cloneFirmDefinition = async (
    firmId: number,
    sourceDefinitionId: number,
    newName: string
  ): Promise<SummaryDefinition> => {
    const source = await this.getFirmDefinitionById(firmId, sourceDefinitionId);
    const spreadsheetDefinition = normalizeDefinition(
      source?.spreadsheet_definition ?? source?.spreadsheetDefinition
    );

    return this.createFirmDefinition(firmId, {
      name: newName,
      spreadsheet_definition: spreadsheetDefinition,
      cloned_from: sourceDefinitionId,
    });
  };

  cloneFundDefinition = async (
    fundId: number,
    sourceDefinitionId: number,
    newName: string
  ): Promise<SummaryDefinition> => {
    const source = await this.getFundDefinitionById(fundId, sourceDefinitionId);
    const spreadsheetDefinition = normalizeDefinition(
      source?.spreadsheet_definition ?? source?.spreadsheetDefinition
    );

    return this.createFundDefinition(fundId, {
      name: newName,
      spreadsheet_definition: spreadsheetDefinition,
      cloned_from: sourceDefinitionId,
    });
  };
}

export default SummaryDefinitionsService;
